import { supabase } from '../lib/supabase'
import { logger } from '../utils/logger'
import { TokenPackage } from '../types'

let hasWarnedTokenPackages = false
const apiBaseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || ''

const apiUrl = (path: string) => `${apiBaseUrl}${path}`

export const defaultTokenPackages: TokenPackage[] = [
  {
    id: 'pack_50',
    tokens: 50,
    price: 25.0,
    bonus: 0,
    description: 'Pacote Básico'
  },
  {
    id: 'pack_100',
    tokens: 100,
    price: 45.0,
    bonus: 10,
    description: 'Pacote Popular'
  },
  {
    id: 'pack_250',
    tokens: 250,
    price: 100.0,
    bonus: 50,
    description: 'Pacote Premium'
  },
  {
    id: 'pack_500',
    tokens: 500,
    price: 180.0,
    bonus: 100,
    description: 'Pacote VIP'
  }
]

export interface TokenTransaction {
  id: string
  user_id: string
  transaction_type: 'purchase' | 'photo_unlock' | 'refund' | 'admin_add'
  amount: number
  description: string
  metadata?: Record<string, any>
  created_at: string
}

class TokenService {
  async getUserBalance(userId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('user_wallets')
        .select('token_balance')
        .eq('user_id', userId)
        .maybeSingle()

      if (error) throw error
      return data?.token_balance || 0
    } catch (error: any) {
      logger.error('Error fetching user balance', error)
      return 0
    }
  }

  async addTokens(_userId: string, _amount: number, _reason: string = 'manual_add'): Promise<boolean> {
    logger.warn('addTokens called on client - this operation is restricted to backend')
    return false
  }

  async deductTokens(_userId: string, _amount: number): Promise<{ success: boolean; newBalance?: number }> {
    logger.warn('deductTokens called on client - this operation is restricted to backend')
    return { success: false }
  }

  async getTransactionHistory(userId: string, limit: number = 20): Promise<TokenTransaction[]> {
    try {
      const { data, error } = await supabase
        .from('token_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return data || []
    } catch (error: any) {
      logger.error('Error fetching transaction history', error)
      return []
    }
  }

  async recordTransaction(
    userId: string,
    type: TokenTransaction['transaction_type'],
    amount: number,
    description: string,
    metadata?: Record<string, any>
  ): Promise<boolean> {
    try {
      const { error } = await supabase.from('token_transactions').insert({
        user_id: userId,
        transaction_type: type,
        amount,
        description,
        metadata: metadata || {}
      })

      if (error) throw error
      logger.info('Transaction recorded', { userId, type, amount, description })
      return true
    } catch (error: any) {
      logger.error('Error recording transaction', error)
      return false
    }
  }

  async purchaseTokens(
    userId: string,
    packageId: string
  ): Promise<{ success: boolean; error?: string; redirectUrl?: string }> {
    try {
      logger.info('Token purchase initiated', { userId, packageId })

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError

      const token = sessionData.session?.access_token
      if (!token) {
        throw new Error('Usuário não autenticado')
      }

      const response = await fetch(apiUrl('/api/mercadopago/preference'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ packageId })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Erro ao criar preferência de pagamento')
      }

      const data = await response.json()
      const useSandbox = import.meta.env.VITE_MP_USE_SANDBOX === 'true'
      const redirectUrl = useSandbox ? data.sandbox_init_point : data.init_point

      if (!redirectUrl) {
        throw new Error('URL de pagamento não disponível')
      }

      logger.info('Mercado Pago preference created', { userId, packageId, preferenceId: data.id })
      return { success: true, redirectUrl }
    } catch (error: any) {
      logger.error('Token purchase failed', error)
      return { success: false, error: error.message }
    }
  }

  async generatePixPayment(amount: number, description: string): Promise<{ qrCode: string; pixKey: string }> {
    try {
      const pixData = {
        qrCode: `00020126580014BR.GOV.BCB.PIX0136${Date.now()}5204000053039865802BR5925NEXT LEVEL BR6009SAO PAULO62070503***6304`,
        pixKey: `pix-${Date.now()}@nextlevelbr.com`
      }

      logger.info('PIX payment generated', { amount, description, pixData })
      return pixData
    } catch (error: any) {
      logger.error('Error generating PIX payment', error)
      throw error
    }
  }

  getTokenPackages(): TokenPackage[] {
    return defaultTokenPackages
  }

  async fetchTokenPackages(): Promise<TokenPackage[]> {
    try {
      const response = await fetch(apiUrl('/api/token-packages'))
      if (!response.ok) {
        throw new Error(`Erro ao carregar pacotes (HTTP ${response.status})`)
      }
      const data = await response.json()
      return Array.isArray(data) ? data : defaultTokenPackages
    } catch (error) {
      if (!hasWarnedTokenPackages) {
        hasWarnedTokenPackages = true
        logger.warn('Using default token packages', error)
      }
      return defaultTokenPackages
    }
  }
}

export const tokenService = new TokenService()
